import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faUserClock, faUser } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';

const formatDateTime = (val) => {
    if (!val) return '—';
    try {
        return new Date(val).toLocaleString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return val;
    }
};

const SupervisorHistoryModal = ({ history, currentSupervisorName, onClose }) => (
    <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4"
        style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
        onClick={onClose}
    >
        <div
            className="w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
            onClick={(e) => e.stopPropagation()}
        >
            {/* Header */}
            <div
                className="flex items-center justify-between px-5 py-4 border-b"
                style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
            >
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faUserClock} className="text-sm" style={{ color: UI_COLORS.TEXT.MUTED }} />
                    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                        Supervisor History
                    </h3>
                </div>
                <button
                    onClick={onClose}
                    className="rounded-lg p-1.5 transition-all duration-200 hover:scale-105"
                    style={{
                        color: UI_COLORS.TEXT.MUTED,
                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.1),
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            {/* Content */}
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                {Array.isArray(history) && history.length > 0 ? (
                    <div className="space-y-2">
                        {history.map((entry, idx) => (
                            <div
                                key={entry.id ?? idx}
                                className="flex items-start gap-3 rounded-xl p-3 border"
                                style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: 'rgba(249,250,251,0.5)' }}
                            >
                                <div
                                    className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold"
                                    style={{
                                        backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.12),
                                        color: UI_COLORS.BRAND.PRIMARY,
                                    }}
                                >
                                    {idx + 1}
                                </div>
                                <div className="flex-1 space-y-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faUser} className="text-[10px]" style={{ color: UI_COLORS.TEXT.MUTED }} />
                                        <span className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                            {entry.supervisor_name || '—'}
                                        </span>
                                    </div>
                                    {entry.assigned_by_name && (
                                        <p className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                            Assigned by: <span className="font-medium">{entry.assigned_by_name}</span>
                                        </p>
                                    )}
                                    {entry.assigned_at && (
                                        <p className="text-[11px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                            {formatDateTime(entry.assigned_at)}
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : currentSupervisorName ? (
                    <div
                        className="flex items-center gap-3 rounded-xl p-3 border"
                        style={{ borderColor: UI_COLORS.BORDERS.LIGHT, backgroundColor: 'rgba(249,250,251,0.5)' }}
                    >
                        <FontAwesomeIcon icon={faUser} className="text-sm" style={{ color: UI_COLORS.TEXT.MUTED }} />
                        <span className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {currentSupervisorName}
                        </span>
                    </div>
                ) : (
                    <p className="text-sm text-center py-8" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        No supervisor history available.
                    </p>
                )}
            </div>
        </div>
    </div>
);

/**
 * Drop-in replacement for the supervisor InfoRow.
 * Shows label + a clickable button with the current supervisor name.
 * On click, opens a modal with the full supervisor_history list.
 *
 * Props:
 *   label              — row label string
 *   supervisorName     — current supervisor name (string)
 *   supervisorHistory  — array of history objects from API
 */
const toNameStr = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'object') return v.name || v.supervisor_name || v.display_name || '';
    return String(v);
};

const SupervisorHistoryButton = ({ label, supervisorName, supervisorHistory = [] }) => {
    const [open, setOpen] = useState(false);

    const nameStr = toNameStr(supervisorName);
    if (!nameStr && (!Array.isArray(supervisorHistory) || supervisorHistory.length === 0)) return null;

    const displayName = nameStr || toNameStr(supervisorHistory[0]?.supervisor_name) || '—';
    const extraCount = Array.isArray(supervisorHistory) ? supervisorHistory.length - 1 : 0;

    return (
        <>
            <div className="flex flex-col">
                <span
                    className="text-[10px] font-bold uppercase tracking-wider"
                    style={{ color: UI_COLORS.TEXT.MUTED }}
                >
                    {label}
                </span>
                <button
                    onClick={() => setOpen(true)}
                    className="mt-1 flex items-center gap-1.5 w-fit rounded-md px-2.5 py-1 text-[12px] font-semibold transition-all duration-150 hover:opacity-80 active:scale-95"
                    style={{ backgroundColor: '#111827', color: '#ffffff' }}
                >
                    <FontAwesomeIcon icon={faUserClock} className="text-[10px] opacity-70" />
                    <span>{displayName}</span>
                    {extraCount > 0 && (
                        <span
                            className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                            style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff' }}
                        >
                            +{extraCount}
                        </span>
                    )}
                </button>
            </div>

            {open && (
                <SupervisorHistoryModal
                    history={supervisorHistory}
                    currentSupervisorName={nameStr}
                    onClose={() => setOpen(false)}
                />
            )}
        </>
    );
};

export default SupervisorHistoryButton;
